import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { CardDescription } from '@/components/ui/card'
import { DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { trpc } from '../trpcReact'
import Content from '@/app/license.mdx'
import { mdxComponents } from '@/components/MdxComponents'
import { toast } from 'sonner'
import { subscriptionKeys } from '@/hooks/useSubscription.ts'
import { useQueryClient } from '@tanstack/react-query'

export const LicenseAgreement = ({
  onAgreed,
  userId,
}: {
  onAgreed: () => void
  userId: string
}) => {
  const [agreed, setAgreed] = useState(false)
  const [canAgree, setCanAgree] = useState(false)
  const licenseRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { mutate, isPending } = trpc.updateLicenseAgreement.useMutation({
    onSuccess: onAgreed,
    onError: (error) => {
      toast.error(`Error updating license agreement: ${error.message}`)
    },
    onSettled: () => {
      // TODO have a separate table for this data so we don't have to refetch all subs
      const queryKey = subscriptionKeys.useSubscriptions(userId)
      return queryClient.invalidateQueries({ queryKey })
    },
  })

  const handleScroll = () => {
    if (licenseRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = licenseRef.current
      // Enable checkbox when scrolled to bottom (with a small threshold)
      if (scrollHeight - scrollTop - clientHeight < 30) {
        setCanAgree(true)
      }
    }
  }

  const handleAgree = () => {
    if (agreed) {
      mutate()
    }
  }

  return (
    <div className="w-full">
      <DialogHeader className="pr-10">
        <DialogTitle>License Agreement</DialogTitle>
        <CardDescription>
          Please read the license agreement and scroll to the bottom to accept
        </CardDescription>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div
          ref={licenseRef}
          className="text-sm h-[400px] overflow-y-auto bg-muted/5 rounded-md p-6"
          onScroll={handleScroll}
        >
          <Content components={mdxComponents} />
        </div>

        <p className="text-xs text-muted-foreground italic pt-4">
          Scroll to the bottom of the license agreement to enable this checkbox
        </p>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="license"
            checked={agreed}
            disabled={!canAgree}
            onCheckedChange={(checked: boolean | 'indeterminate') => setAgreed(checked === true)}
          />
          <Label
            htmlFor="license"
            className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${
              !canAgree ? 'text-muted-foreground' : ''
            }`}
          >
            I have read and agree to the license terms
          </Label>
        </div>

        <Button onClick={handleAgree} disabled={!agreed || isPending} className="w-full">
          {isPending ? 'Processing...' : 'Continue'}
        </Button>
      </div>
    </div>
  )
}
